import { useState, useCallback } from 'react';

import { useHuntJournalStore } from '@/stores/hunt-journal-store';
import type { JournalEntryTag } from '@/types/hunt-journal-entry';

const MAX_BODY = 280;
const NEAR_THRESHOLD = 260;

const TAGS: { value: JournalEntryTag; label: string }[] = [
  { value: 'note', label: 'Note' },
  { value: 'sighting', label: 'Sighting' },
  { value: 'weather', label: 'Weather' },
  { value: 'camp', label: 'Camp' },
  { value: 'gear', label: 'Gear' },
];

type CounterState = 'ok' | 'near' | 'over';

function getCounterState(length: number): CounterState {
  if (length > MAX_BODY) return 'over';
  if (length > NEAR_THRESHOLD) return 'near';
  return 'ok';
}

interface JournalComposerProps {
  huntId: string;
}

export function JournalComposer({ huntId }: JournalComposerProps) {
  const addEntry = useHuntJournalStore((s) => s.addEntry);

  const [body, setBody] = useState('');
  const [selectedTag, setSelectedTag] = useState<JournalEntryTag>('note');
  const [isPosting, setIsPosting] = useState(false);

  const length = body.length;
  const counterState = getCounterState(length);
  const isDisabled = length === 0 || length > MAX_BODY || isPosting;

  const handlePost = useCallback(async () => {
    if (isDisabled) return;
    setIsPosting(true);
    try {
      await addEntry({
        huntId,
        body,
        tag: selectedTag,
      });
      // Reset after successful post
      setBody('');
      setSelectedTag('note');
    } finally {
      setIsPosting(false);
    }
  }, [isDisabled, addEntry, huntId, body, selectedTag]);

  return (
    <div className="flex flex-col gap-3" data-testid="journal-composer">
      {/* Tag row */}
      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Entry tag">
        {TAGS.map((tag) => (
          <button
            key={tag.value}
            type="button"
            role="radio"
            aria-checked={selectedTag === tag.value}
            data-testid={`tag-${tag.value}`}
            onClick={() => setSelectedTag(tag.value)}
            className={[
              'px-2.5 py-1 rounded-full text-body-xs font-medium border transition-colors',
              selectedTag === tag.value
                ? 'bg-[var(--color-accent)] text-white border-[var(--color-accent)]'
                : 'bg-[var(--color-elevated)] text-[var(--color-text-secondary)] border-[var(--color-border)] hover:border-[var(--color-accent)]',
            ].join(' ')}
          >
            {tag.label}
          </button>
        ))}
      </div>

      {/* Body textarea */}
      <textarea
        data-testid="journal-composer-body"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Capture a moment from the field…"
        rows={3}
        // M-08 (Defect Batch M): inward outline so focus ring is never clipped (ADR-035).
        className="w-full px-3 py-2 rounded-md bg-[var(--color-elevated)] border border-[var(--color-border)] text-body-sm text-[var(--color-text-primary)] placeholder-[var(--color-text-placeholder)] focus:outline-2 focus:outline-[var(--color-accent)] focus:outline-offset-[-2px] resize-y"
      />

      {/* Footer row: counter + post button */}
      <div className="flex items-center justify-between gap-3">
        <span
          data-testid="journal-composer-counter"
          data-counter-state={counterState}
          className={[
            'text-body-xs tabular-nums',
            counterState === 'ok' && 'text-[var(--color-text-muted)]',
            counterState === 'near' && 'text-amber-500',
            counterState === 'over' && 'text-red-500',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {length} / {MAX_BODY}
        </span>

        <button
          type="button"
          data-testid="journal-composer-post-btn"
          disabled={isDisabled}
          onClick={() => void handlePost()}
          className="px-4 py-1.5 rounded-md bg-[var(--color-accent)] text-white text-body-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
        >
          {isPosting ? 'Posting…' : 'Post'}
        </button>
      </div>
    </div>
  );
}
